import { z } from "zod";
import { prepaymentFor } from "./bidding-mode";
import { FxConversionSchema, LedgerMethodSchema, PaymentCurrencySchema, PaymentEntryTypeSchema, PaymentOriginalSchema, } from "./payment";
import { PricingPlanTierSchema } from "./pricing-rules";
/**
 * Admin finance contract — the wire shapes behind the admin «Фінанси» screen.
 *
 * ── The one rule this module exists to enforce ───────────────────────────────
 * RAW VALUES ONLY. No display strings cross this boundary: no currency symbols,
 * no day-month date formats, no localized month abbreviations, no pre-rendered
 * status phrases. Dates travel as `z.coerce.date()` (ISO-8601 on the wire),
 * money as `number`, everything categorical as an enum KEY.
 *
 * The reason is not tidiness. `packages/i18n` already ships an `en` catalogue
 * alongside `uk`, so a Ukrainian string baked into a response is undeliverable
 * to an English operator — the frontend cannot translate what the backend has
 * already rendered. `apps/admin/src/features/finance/types.ts` does exactly
 * that today (a pre-formatted `sum`, a day-month `due`, a three-letter
 * Ukrainian month `label`, a `method` holding the Ukrainian word for "card");
 * those fields are presentation and they stop here. Anything derivable at
 * render time — an urgency tone, a days-past-due count, a tariff caption — is
 * NOT on the wire either: it is a pure function of `dueAt`, `status` and
 * `tier`, and duplicating it invites the two copies to disagree.
 *
 * ── Money ───────────────────────────────────────────────────────────────────
 * Amounts are WHOLE UNITS as plain `number`, never minor units. Cents are not
 * used anywhere in this repo — {@link DebtBreakdownSchema}'s totals are plain
 * numbers — so a minor-unit field here would be the odd one out and the
 * conversion would silently re-denominate every value that flows between them.
 * Do not "fix" this to cents without converting `relay.ts` in the same change.
 *
 * ── Currency: CHF end to end, and a field is named for its currency ────────
 * Owner rule 01 (2026-09-27): invoices, receivables, the journal's receivable
 * lines and revenue are CHF. Every money field on this module is therefore
 * `*Chf`, and the suffix is not decoration — it is what makes a sum across
 * mismatched units visible at the call site rather than hidden behind a
 * neutral name. The rule is "rename, never mix": a figure that was EUR at its
 * source (the CarsFinders service fee, a pricing plan's `monthlyFee`) may sit
 * in a `*Chf` field ONLY once it has been converted, and only with the
 * conversion recorded beside it as an {@link FxConversionSchema}
 * (see {@link convertEurToChf}). Converting is allowed; converting SILENTLY
 * is not, and relabelling a EUR number as CHF is not a conversion at all.
 *
 * The CHF legs of a car invoice — the hammer price and the Swiss platform fee
 * (`relay.ts#computeClientDebt`) — are CHF at the source and travel with
 * `fx: null`. The deposit (`users.biddingPower`, "the deposit in CHF" in
 * `exposure.ts`) is CHF at the source too.
 *
 * Where the source is genuinely multi-currency — a payments-ledger row is
 * `CHF|EUR|USD|UAH` by schema — no suffix can be honest, so the amount travels
 * with an explicit `currency` key instead (see
 * {@link FinanceTransactionSchema}). An object that names its own unit is as
 * safe as a suffixed field. A producer that SUMS such rows into a `*Chf`
 * figure folds the CHF rows only, and counts and logs the rest rather than
 * adding them.
 *
 * ── Derived values are never stored ─────────────────────────────────────────
 * One rule, applied everywhere: if a value is a pure function of the clock, it
 * is computed at render and never persisted. `overdue` is the case that keeps
 * trying to sneak in — see {@link InvoiceSchema}.
 *
 * ── What is NOT here ────────────────────────────────────────────────────────
 * `DealInvoiceSchema` in `deal.ts` is untouched and stays that way: it is
 * one-per-deal and car-only. {@link InvoiceSchema} below is the general billing
 * entity (subscriptions, extras, ad-hoc lines, several per client) and gets its
 * own collection.
 */
/* ────────────────────────────── Enumerations ─────────────────────────────── */
/**
 * Where a client stands with us overall, in the artboard's `FST` order.
 *
 * `none` means no invoice was ever issued — distinct from `paid`, which means
 * everything issued has landed.
 */
export const FinanceClientStatusSchema = z.enum([
    "overdue",
    "partial",
    "pending",
    "refund",
    "paid",
    "none",
]);
export const FINANCE_CLIENT_STATUSES = FinanceClientStatusSchema.options;
/**
 * Where ONE invoice stands as the SCREEN shows it, in the artboard's `IST`
 * order. This is a DISPLAY status: `overdue` is derived from `dueAt` against
 * now, so this enum is what a renderer produces, never what a database row
 * holds. The persisted ladder is {@link FinanceInvoiceStoredStatusSchema}.
 *
 * `checking` («Платіж перевіряється») is the client saying «Я сплатив»: money
 * may be on its way, and the operator has to look before anybody chases.
 *
 * `cancelled` («Анульовано») is the stored status passed through: the client
 * refused to pay for the car and the deposit was withheld (owner decision (a)).
 * Appended last, so every existing value keeps its position.
 */
export const FinanceInvoiceStatusSchema = z.enum([
    "overdue",
    "checking",
    "pending",
    "partial",
    "paid",
    "cancelled",
]);
export const FINANCE_INVOICE_STATUSES = FinanceInvoiceStatusSchema.options;
/**
 * Where ONE invoice stands as a STORED fact: `pending → partial → checking →
 * paid`.
 *
 * `checking` IS stored, unlike `overdue`: it is an event (the client clicked
 * «Я сплатив», recorded with `clientMarkedPaidAt`), not a function of the
 * clock, so nothing about it goes stale at midnight. It still counts as
 * UNPAID for every money fold — a claim is not a settlement.
 *
 * `overdue` is absent on purpose. It is `dueAt < now`, which turns true when
 * the clock passes midnight and no write happens — so a stored `overdue` is
 * stale by construction, and a stored `pending` on a past-due invoice is not
 * wrong, it is simply the payment ladder saying nothing has landed. A renderer
 * combines this with `dueAt` to produce {@link FinanceInvoiceStatusSchema}.
 *
 * `cancelled` is OFF the payment ladder (owner decision (a), 2026-09-28): the
 * client refused to pay for the won car, the operator confirmed it on the deal
 * and the deposit was withheld as the penalty. Nothing is owed on a cancelled
 * invoice ({@link invoiceOutstandingChf} is 0), it is not a receivable, it earns
 * no commission, and nothing may be settled or reminded against it. What was
 * withheld is recorded on the invoice as {@link InvoiceForfeitSchema}.
 */
export const FinanceInvoiceStoredStatusSchema = z.enum([
    "pending",
    "partial",
    "checking",
    "paid",
    "cancelled",
]);
export const FINANCE_INVOICE_STORED_STATUSES = FinanceInvoiceStoredStatusSchema.options;
/**
 * The DISPLAY status of one invoice: the stored ladder with `overdue` added
 * back from `dueAt` against now.
 *
 * This is the ONE definition of that rule and it lives in the contract on
 * purpose. It is produced at READ time, never stored: being past due turns
 * true when the clock passes midnight and no write happens, so a persisted
 * `overdue` is stale by construction — which is exactly why
 * {@link FinanceInvoiceStoredStatusSchema} has no slot for it.
 *
 * It lives HERE rather than in `apps/api` because the admin screen needs the
 * same rule and cannot import `apps/api/src/modules/*` (cross-app code goes
 * through `@carsfinders/*`). A screen that re-implemented it by hand would be
 * the duplicated truth this contract exists to prevent — and the copy that
 * drifted would render a client `overdue` on an invoice that has landed.
 *
 * Precedence, highest first:
 * - `cancelled` outranks everything: nothing is owed on it, so it is never
 *   overdue (owner decision (a));
 * - `paid` outranks the rest: an invoice that has landed is not overdue, no
 *   matter what its deadline said;
 * - `checking` outranks the clock: the client says they paid, so the row asks
 *   the operator to LOOK rather than to chase;
 * - past `dueAt` is `overdue` — `partial` does NOT escape it, because part
 *   payment is not payment and the remainder is still late;
 * - otherwise the stored status.
 *
 * The parameter is structural rather than `Pick<Invoice, ...>` so this can sit
 * beside the enums it maps between; every `Invoice` satisfies it.
 */
export function deriveInvoiceStatus(invoice, now = new Date()) {
    if (invoice.status === "cancelled")
        return "cancelled";
    if (invoice.status === "paid")
        return "paid";
    if (invoice.status === "checking")
        return "checking";
    return invoice.dueAt.getTime() < now.getTime() ? "overdue" : invoice.status;
}
/**
 * The three ways CarsFinders earns money.
 *
 * `retail_service` — the fixed service fee charged to a retail client.
 * `dealer_subscription` — a recurring dealer plan fee.
 * `dealer_cut` — the percentage commission on a dealer's winning bid.
 */
export const RevenueStreamSchema = z.enum([
    "retail_service",
    "dealer_subscription",
    "dealer_cut",
    /**
     * The deposit withheld when a client refuses to pay for a won car (owner
     * decision (a), 2026-09-28) — recognized in the month it was withheld.
     */
    "deposit_forfeit",
]);
export const REVENUE_STREAMS = RevenueStreamSchema.options;
/**
 * What a journal line records. `in` is money received, `out` is money paid or
 * refunded, `issued` is an invoice raised — a receivable, not a movement.
 */
export const TransactionKindSchema = z.enum(["in", "out", "issued"]);
export const TRANSACTION_KINDS = TransactionKindSchema.options;
/** The window a revenue or summary figure was aggregated over. */
export const FinancePeriodSchema = z.enum(["month", "quarter", "year"]);
export const FINANCE_PERIODS = FinancePeriodSchema.options;
/** What an invoice bills for. */
export const InvoiceKindSchema = z.enum(["car", "extra", "sub", "other"]);
export const INVOICE_KINDS = InvoiceKindSchema.options;
/**
 * The rail money actually moved over, as a key.
 *
 * Replaces the free-text `method` on the current frontend shape, whose fixtures
 * hold `'SWIFT'`, the Ukrainian word for "card", and an em dash standing in for
 * "nothing has moved yet". The first two are a localization bug waiting to
 * happen and the third is a rendering of absence: on the wire, absence is null.
 */
export const PaymentRailSchema = z.enum([
    "swift",
    "card",
    "cash",
    "deposit_offset",
]);
export const PAYMENT_RAILS = PaymentRailSchema.options;
/**
 * A calendar month as `YYYY-MM`.
 *
 * Deliberately NOT a `Date`: a revenue bucket is a month, not an instant, and
 * a `Date` would drag a timezone into a value that has none — the September
 * bucket must not become August for an operator west of UTC.
 */
export const MonthKeySchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, {
    message: "month must be YYYY-MM",
});
/**
 * An i18n KEY, not a rendered caption.
 *
 * `reasonKey: "Депозит"` used to parse, and then reached an English operator
 * untranslated — precisely the failure this module exists to prevent, and the
 * one place still left open while {@link MonthKeySchema} and
 * {@link PaymentRailSchema} were both already gated mechanically. Past the
 * wire nothing can tell a key from a caption, so the shape is pinned here: the
 * characters an i18n key namespace uses, and nothing else.
 *
 * This is deliberately NOT a charset check — "Hammer price" is a caption too.
 * A space is what separates the two in practice. Operator free text belongs in
 * `InvoiceLine.label`, which stays unconstrained for exactly that reason.
 */
const I18nKeySchema = z.string().regex(/^[A-Za-z0-9_.-]+$/, {
    message: "must be an i18n key, not a rendered caption",
});
/* ──────────────────────────── Currency conversion ────────────────────────── */
/**
 * {@link FxConversionSchema} lives in `./payment` (the ledger row carries one
 * too, and `payment.ts` cannot import this module without a cycle); it is
 * re-exported here so every existing import keeps resolving.
 */
export { FxConversionSchema } from "./payment";
/** Whole units, rounded to the cent — `Math.round`, half away from zero for positives. */
function roundToCent(value) {
    return Math.round(value * 100) / 100;
}
/**
 * Convert a EUR figure to CHF ONCE, and record how.
 *
 * `chfToEurRate` is the rule set's `swissExport.chfToEurRate` (EUR per CHF,
 * e.g. 1.05) — the number `computeClientDebt` multiplied the hammer price by
 * when it priced the EUR service fee. Going back, the multiplier is its
 * reciprocal, and THAT is what lands in `fx.rate`, so the contract's
 * `valueChf ≈ fromAmount × rate` stays true of every line.
 *
 * `valueChf` is `round(fromAmount × rate, 2)`. The API's aggregation
 * pipelines that convert a stored breakdown in MongoDB
 * (`apps/api/.../admin-finance/client-rollup.service.ts#eurToChfExpression`)
 * mirror this exact arithmetic — reciprocal first, then multiply, then round
 * half-up to the cent — so a chart bar and an invoice line built from the
 * same breakdown cannot disagree by a cent.
 *
 * Returns `null` when the conversion cannot be made honestly: a rate that is
 * not a finite positive number, or a negative / non-finite amount (an FX
 * record's `fromAmount` is a magnitude by schema). The caller decides what an
 * unconvertible figure means; this function never guesses a rate.
 */
export function convertEurToChf(amountEur, chfToEurRate, ratesVersion) {
    if (!Number.isFinite(chfToEurRate) || chfToEurRate <= 0)
        return null;
    if (!Number.isFinite(amountEur) || amountEur < 0)
        return null;
    const rate = 1 / chfToEurRate;
    return {
        valueChf: roundToCent(amountEur * rate),
        fx: { fromCurrency: "EUR", fromAmount: amountEur, rate, ratesVersion },
    };
}
/* ──────────────────────────────── Invoices ───────────────────────────────── */
/**
 * One line of an invoice.
 *
 * Labels travel as i18n KEYS, not resolved text, for the same reason the whole
 * module does. `label` is the single exception and is explicitly an operator's
 * own free text: when they type a description it overrides `labelKey`, and
 * there is nothing to translate because a human wrote it.
 */
export const InvoiceLineSchema = z.object({
    /** Stable identity of the line within its invoice. */
    key: z.string(),
    /** i18n key for the line caption. */
    labelKey: I18nKeySchema,
    /** Operator's free-text description; overrides `labelKey` when present. */
    label: z.string().nullish(),
    /** i18n key explaining how `valueChf` was arrived at. */
    basisKey: I18nKeySchema,
    /**
     * Interpolation values for `basisKey` — numbers stay numbers.
     *
     * This is an i18n argument bag for ONE caption, not a place to put money the
     * API adds up: its numbers are arguments (a percentage, a count), and because
     * its keys are free-form they cannot carry a currency suffix the way a field
     * can. Money belongs in a suffixed field. The currency guard in
     * `__tests__/admin-finance.spec.ts` acknowledges this record by name for
     * exactly that reason.
     */
    basisParams: z.record(z.union([z.string(), z.number()])).nullish(),
    valueChf: z.number().finite(),
    /**
     * How `valueChf` was converted, when it was. Required but nullable: `null`
     * is an author stating "this line was CHF to begin with" (a hammer price,
     * the Swiss platform fee, a deposit, a figure an operator typed in CHF),
     * which is a different claim from an omitted key meaning nobody looked.
     */
    fx: FxConversionSchema.nullable(),
    /** True when this line is CarsFinders revenue rather than a pass-through. */
    ours: z.boolean(),
    /**
     * LEGACY, parse-only. Car-invoice lines are fixed and system-calculated
     * (owner, 2026-09-28): nothing writes these any more. They are still READ
     * because per-line admin corrections (`lineOverrides`, #141) were live in
     * production for a few hours on 2026-09-28, so an invoice issued in that
     * window may carry the replaced server figure (`computedChf`) and the admin
     * who corrected it (`adjustedBy`). Issued invoices are documents and are
     * never rewritten. Staff-internal: the client read strips both.
     */
    computedChf: z.number().finite().nullish(),
    /** LEGACY, parse-only — see `computedChf`. */
    adjustedBy: z.string().nullish(),
});
/**
 * The money figures of a car invoice from its lines and the client's deposit:
 * the lines' cent-rounded sum, the prepayment `prepaymentFor(deposit, total)`
 * takes off the deposit, and what is left to pay. The API's preview calls
 * this; the issuing transaction uses the same
 * `prepaymentFor` on the same sum, read against the in-session deposit.
 */
export function carInvoiceTotals(lines, depositChf) {
    const totalChf = roundToCent(lines.reduce((sum, line) => sum + line.valueChf, 0));
    const { prepaymentChf } = prepaymentFor(depositChf, totalChf);
    const dueChf = totalChf - prepaymentChf > 0 ? roundToCent(totalChf - prepaymentChf) : 0;
    return { totalChf, prepaymentChf, dueChf };
}
/**
 * Why a car invoice with these lines may NOT be issued, or `null` when it may.
 *
 * `total_not_positive`: the cent-rounded total is not a finite number above
 * zero. A zero-total car invoice is issued `pending` (no prepayment to cover
 * it), can never be marked paid (nothing is owed, so settlement answers
 * "already paid"), strands its deal at `invoice_issued`, and counts as unpaid
 * against the client's refund. The API refuses it before anything is written;
 * nothing else can produce one now that car lines are fixed, but the guard stays.
 */
export function carInvoiceIssueProblem(lines) {
    const totalChf = roundToCent(lines.reduce((sum, line) => sum + line.valueChf, 0));
    return Number.isFinite(totalChf) && totalChf > 0 ? null : "total_not_positive";
}
/**
 * `GET /api/admin/finance/invoices/car-preview?dealId=` — what issuing a car
 * invoice for this deal would write, BEFORE anything is written. Built by the
 * very function the issue path uses; the server stays the authority (the
 * deposit is re-read in the issuing transaction).
 */
export const CarInvoicePreviewSchema = z.object({
    /** The deal's canonical id. */
    dealId: z.string(),
    /** The client the deal belongs to. */
    userId: z.string(),
    lines: z.array(InvoiceLineSchema),
    totalChf: z.number().finite(),
    /** The client's deposit (`users.biddingPower`) at the time of the preview. */
    depositChf: z.number().finite(),
    prepaymentChf: z.number().finite().nonnegative(),
    dueChf: z.number().finite().nonnegative(),
});
/**
 * One payment an operator recorded against an invoice («Оплату отримано»,
 * admin deals & deposits — phase 6). CHF, a positive magnitude.
 *
 * Settlements are money that moved OUTSIDE the platform (a bank transfer the
 * operator saw), recorded on the invoice itself — they are NOT `payments`
 * ledger rows, because the ledger is the client's deposit and a settlement
 * never touches it. `paidChf` is their running sum.
 */
export const InvoiceSettlementSchema = z.object({
    amountChf: z.number().finite().positive(),
    at: z.coerce.date(),
    /** Admin userId who recorded it. */
    by: z.string(),
    note: z.string().nullish(),
    /**
     * The admin client payment this settlement is one allocation of — the key the
     * finance journal groups by and the replay guard looks up. Absent on
     * settlements recorded by «Оплату отримано» and on every older document.
     */
    paymentId: z.string().uuid().optional(),
    /** How the money moved, when the payment endpoint recorded it. */
    method: LedgerMethodSchema.optional(),
    /** Payment / receipt number the operator recorded. */
    reference: z.string().max(64).optional(),
    /** When the client payment was received (the operator's date), payment endpoint only. */
    receivedAt: z.coerce.date().optional(),
    /** The WHOLE client payment in its original currency, payment endpoint only. */
    original: PaymentOriginalSchema.optional(),
});
/**
 * What was withheld when the client refused to pay this (car) invoice — owner
 * decision (a), 2026-09-28. Written once, by the admin-confirmed
 * `POST /api/admin/deals/:dealId/forfeit-deposit`, in the same transaction that
 * cancels the invoice.
 *
 * The penalty is the client's WHOLE deposit (owner, 2026-09-28: the whole
 * `biddingPower` pool) plus whatever they already paid on the invoice — by then
 * in three places:
 * - `prepaymentChf` — the part the invoice issue already took off the deposit
 *   (`prepaymentFor`). It is not refunded: the invoice is cancelled and the
 *   prepayment is retained. No new ledger row — it left the deposit at issue;
 * - `depositChf` — whatever was still on `users.biddingPower`, debited now by
 *   ONE `penalty` ledger row (`deal:<dealId>:forfeit`). 0 when nothing was left;
 * - `paidChf` — settlements already received against the invoice (owner,
 *   2026-09-28: a partial payment is retained too, never refunded). No new
 *   money moves: the cash landed earlier and is already an `in` line in the
 *   journal. Defaults to 0 so a record written before this field reads right.
 *
 * `totalChf` = all three, the PENALTY: what the revenue chart and the summary
 * card book as `deposit_forfeit` (reclassification — the cancelled invoice's
 * commission no longer counts, so nothing is added twice). The journal, which
 * lists money MOVEMENTS, shows only {@link forfeitJournalChf} — the paid part
 * already has its own `in` line.
 */
export const InvoiceForfeitSchema = z.object({
    at: z.coerce.date(),
    /** Admin userId who confirmed the refusal. */
    by: z.string(),
    /** The operator's reason, trimmed, ≤ 500 characters. */
    reason: z.string(),
    depositChf: z.number().finite().nonnegative(),
    prepaymentChf: z.number().finite().nonnegative(),
    /** Settlements already received on the invoice, retained. */
    paidChf: z.number().finite().nonnegative().default(0),
    totalChf: z.number().finite().nonnegative(),
});
/**
 * The part of a forfeit the money JOURNAL shows as its own `in` line: the
 * deposit and the retained prepayment. The paid part is excluded because each
 * settlement is already an `in` line of its own — listing it again would show
 * the same cash arriving twice. Never below 0, cent-rounded.
 */
export function forfeitJournalChf(forfeit) {
    const value = forfeit.totalChf - (forfeit.paidChf ?? 0);
    return value > 0 ? roundToCent(value) : 0;
}
/**
 * The persisted invoice entity. CHF end to end (owner rule 01, 2026-09-27).
 *
 * NOT `DealInvoiceSchema`: that one is one-per-deal, car-only and stores a
 * `DebtBreakdown`. This is the general billing record — several per client,
 * covering subscriptions and extras as well as cars — and lives in its own
 * collection precisely so neither shape has to bend to the other.
 *
 * Three money figures, all CHF, and what is still owed is
 * `totalChf − prepaymentChf − paidChf` ({@link invoiceOutstandingChf}):
 * - `totalChf` is the sum of the lines;
 * - `prepaymentChf` is the client's deposit applied to this invoice when it was
 *   issued (`bidding-mode.ts#prepaymentFor`). It defaults to 0 — nothing
 *   writes it until the prepayment write lands, and a document stored before
 *   then carries none;
 * - `paidChf` is the running total received against the invoice AFTER the
 *   prepayment.
 *
 * `status` is the STORED ladder — `pending → partial → checking → paid` — and
 * it excludes `overdue`. The tempting argument for storing `overdue` ("a
 * partially-paid invoice that later goes past due changes status without
 * either number moving") is in fact the argument AGAINST it: nothing moves, so
 * nothing triggers a write, so a stored `overdue` goes stale the moment the
 * clock passes midnight. Being overdue is `dueAt` against now and is derived
 * at render — the same rule that keeps `tone` and `overdueDays` off
 * {@link FinanceReceivableSchema}. {@link FinanceInvoiceStatusSchema} is the
 * display status that adds `overdue` back on the way to the screen.
 */
export const InvoiceSchema = z.object({
    /** Human invoice number, e.g. `CF-2409-118`. */
    no: z.string(),
    userId: z.string(),
    /** The deal billed, when this invoice bills for a car; null for subs/extras. */
    dealId: z.string().nullish(),
    kind: InvoiceKindSchema,
    lines: z.array(InvoiceLineSchema),
    totalChf: z.number().finite(),
    /** Deposit applied as prepayment at issue. Zero until the prepayment write lands. */
    prepaymentChf: z.number().finite().nonnegative().default(0),
    /** How much has landed on top of the prepayment. Zero on a freshly issued invoice. */
    paidChf: z.number().finite(),
    /** The persisted ladder only; `overdue` is derived from `dueAt` at render. */
    status: FinanceInvoiceStoredStatusSchema,
    issuedAt: z.coerce.date(),
    dueAt: z.coerce.date(),
    /**
     * When the client clicked «Я сплатив» — the event behind a stored
     * `checking`. Absent on every invoice the client has not claimed.
     */
    clientMarkedPaidAt: z.coerce.date().nullish(),
    /**
     * Every payment recorded against the invoice, oldest first (phase 6).
     * Optional: absent on an invoice nothing has been paid against, and on every
     * document stored before phase 6.
     */
    settlements: z.array(InvoiceSettlementSchema).optional(),
    /** When an operator last pressed «Нагадати» — throttles the next reminder. */
    remindedAt: z.coerce.date().nullish(),
    /**
     * Set together with `status: 'cancelled'` when the client refused to pay and
     * the deposit was withheld (owner decision (a)). Absent on every other invoice.
     */
    forfeit: InvoiceForfeitSchema.nullish(),
});
/**
 * What one invoice still owes, CHF: `total − prepayment − paid`, never below 0.
 *
 * The ONE definition, used by the API's client rollup and by the admin screen
 * alike: two copies of this subtraction would disagree the first time one of
 * them forgot the prepayment. A `checking` invoice still owes — a claim is not
 * a settlement. A `cancelled` one owes nothing (owner decision (a)): pass
 * `status` whenever the caller has it.
 */
export function invoiceOutstandingChf(invoice) {
    if (invoice.status === "cancelled")
        return 0;
    const remaining = invoice.totalChf - (invoice.prepaymentChf ?? 0) - invoice.paidChf;
    return remaining > 0 ? roundToCent(remaining) : 0;
}
/* ─────────────────────────── Clients and receivables ─────────────────────── */
/**
 * One row of the admin client table, with its invoices.
 *
 * The frontend's `role` caption and `dueText` phrase are absent by design —
 * both are rendered from `tier` and `nextDueAt`.
 */
export const FinanceClientSchema = z.object({
    userId: z.string(),
    name: z.string(),
    /** Decides the client's commission and subscription pricing. */
    tier: PricingPlanTierSchema,
    status: FinanceClientStatusSchema,
    /**
     * Deposit held, CHF — `users.biddingPower`, which is CHF at the source and
     * is the only stored deposit figure. Zero means none was ever taken.
     */
    depositChf: z.number().finite(),
    /**
     * Of `depositChf`, how much is refundable rather than committed against an
     * open invoice.
     *
     * NULLABLE because nothing computes it yet: the payments ledger is
     * append-only `deposit|refund|fee` entries with no committed/free split, so
     * there is no formula to apply. `null` says "unknown" honestly, where a `0`
     * would be an invented number the screen would render as fact. It becomes
     * required the day a commitment record exists to derive it from.
     */
    depositRefundableChf: z.number().finite().nullish(),
    /** Free text from the deposit ledger, written by an operator. */
    depositNote: z.string().nullish(),
    /** Issued minus received across `invoices`, CHF. */
    outstandingChf: z.number().finite(),
    /** The part of `outstandingChf` already past its due date. */
    overdueChf: z.number().finite(),
    /** Earliest unpaid due date; null when nothing is outstanding. */
    nextDueAt: z.coerce.date().nullish(),
    invoices: z.array(InvoiceSchema),
});
/**
 * One entry of the upcoming-receivables list.
 *
 * The frontend's `tone` ramp and `overdueDays` counter are deliberately absent:
 * both are pure functions of `dueAt` against now, and a stored copy would be
 * stale the moment the response was cached.
 */
export const FinanceReceivableSchema = z.object({
    invoiceNo: z.string(),
    userId: z.string(),
    clientName: z.string(),
    amountChf: z.number().finite(),
    dueAt: z.coerce.date(),
});
/* ──────────────────────────────── Journal ────────────────────────────────── */
/**
 * One line of the money journal.
 *
 * `amount` is a MAGNITUDE and is non-negative; `kind` carries the direction.
 * The frontend stores a pre-signed, pre-formatted string instead, which is both
 * a display value and a second source of truth for the sign — a renderer that
 * negated an already-negative number would silently invert an outgoing payment.
 * One direction field, one magnitude field, no ambiguity.
 *
 * It carries an explicit `currency` instead of a `*Chf` suffix because its
 * source is the payments ledger, whose rows are `CHF|EUR|USD|UAH` by schema
 * (`payment.ts#PaymentLedgerEntrySchema`). A suffix here would be a guess, and
 * `payments-ledger.repository.ts#findByUser` already discards the real answer.
 * An `issued` line is an invoice, and invoices are CHF, so it says `CHF`.
 *
 * `kind` alone cannot say what a line was FOR — a deposit credit and an invoice
 * payment are both `in`, and on an OPERATOR's screen so is a `fee`, because the
 * dealer subscription and the marketplace consignment charge are CarsFinders
 * income (only a `refund` is `out`; the client's own balance reads a fee the
 * other way, which is precisely the distinction one direction bit cannot carry)
 * — yet the journal table renders a description cell for every row. So the two
 * facts that survive the flattening travel too: `source`, the ledger's own
 * `PaymentEntryType` (reused, never copied), and `reasonKey`, an i18n KEY. A
 * rendered caption would be undeliverable to an English operator, for the
 * reason at the top of this file.
 */
export const FinanceTransactionSchema = z.object({
    id: z.string(),
    at: z.coerce.date(),
    kind: TransactionKindSchema,
    /** Magnitude in `currency`; never signed — `kind` carries the direction. */
    amount: z.number().finite().nonnegative(),
    /** The unit `amount` is in. Ledger rows are multi-currency by schema. */
    currency: PaymentCurrencySchema,
    /**
     * The ledger entry type behind this line, reusing `payment.ts`'s own enum.
     * Null for a movement the ledger does not model — an invoice payment, or an
     * `issued` line, which is a receivable rather than a movement at all.
     * Required-but-nullable so the question is answered rather than skipped.
     */
    source: PaymentEntryTypeSchema.nullable(),
    /** i18n KEY for the description cell, e.g. `adminFinance.tx.depositCredit`. */
    reasonKey: I18nKeySchema,
    userId: z.string(),
    clientName: z.string(),
    /** The invoice this line settles or raises, when there is one. */
    invoiceNo: z.string().nullish(),
    /** Null when nothing has moved yet — an `issued` line has no rail. */
    rail: PaymentRailSchema.nullish(),
});
/* ──────────────────────────────── Revenue ────────────────────────────────── */
/** An amount split across the revenue streams, CHF. Kept flat, one field each. */
export const RevenueByStreamSchema = z.object({
    retailServiceChf: z.number().finite(),
    dealerSubscriptionChf: z.number().finite(),
    dealerCutChf: z.number().finite(),
    /**
     * `deposit_forfeit` (owner decision (a)). Optional so a reader built before it
     * keeps compiling; the API always sends it. Always `received` — the money was
     * already held when it was withheld.
     */
    depositForfeitChf: z.number().finite().optional(),
});
/**
 * One bar of the revenue chart: the three streams for one calendar month, each
 * split into the part that has settled and the part still outstanding.
 *
 * `month` is a `YYYY-MM` key, not a localized abbreviation — the frontend's
 * three-letter Ukrainian month is a label an English operator cannot read.
 *
 * ── Why the split is on the bar and not derived later ─────────────────────
 * Revenue is recognized when the invoice is RAISED, never when the money
 * lands, so `received`/`pending` is a SETTLEMENT split WITHIN the issue month
 * rather than a second bucketing: an invoice issued in August and paid in
 * September stays in August's bar and merely moves from `pending` to
 * `received`. The month's total per stream does not change when money arrives.
 *
 * A flat shape — one number per stream — had nowhere to put that, so the
 * producer grew a private split type and the split never reached the wire.
 * Both halves reuse {@link RevenueByStreamSchema}, so a new stream is added in
 * exactly one place and the two halves cannot drift apart.
 *
 * A renderer that wants the whole bar calls {@link revenueMonthTotals} rather
 * than re-adding the pairs at each call site — one definition of the total,
 * one rounding rule.
 */
export const RevenueMonthSchema = z.object({
    month: MonthKeySchema,
    /** Invoiced in `month` and settled since. */
    received: RevenueByStreamSchema,
    /** Invoiced in `month` and still outstanding. Never re-bucketed when it lands. */
    pending: RevenueByStreamSchema,
});
/**
 * The bar's total per stream — what the old flat shape meant.
 *
 * Rounded to the cent on the way out: both halves are already cent-rounded by
 * the producer, and adding two of them in IEEE-754 reintroduces a tail
 * (`900 + 450.0` is exact, `0.1 + 0.2` is not) that would reach the screen as
 * CHF 1350.0000000000002.
 */
export function revenueMonthTotals(month) {
    const cents = (a, b) => roundToCent(a + b);
    return {
        retailServiceChf: cents(month.received.retailServiceChf, month.pending.retailServiceChf),
        dealerSubscriptionChf: cents(month.received.dealerSubscriptionChf, month.pending.dealerSubscriptionChf),
        dealerCutChf: cents(month.received.dealerCutChf, month.pending.dealerCutChf),
        depositForfeitChf: cents(month.received.depositForfeitChf ?? 0, month.pending.depositForfeitChf ?? 0),
    };
}
/* ──────────────────────────────── Summary ────────────────────────────────── */
/**
 * The finance screen's headline figures for one `period`, all CHF.
 *
 * Receivables and revenue are CHF because invoices are billed in CHF; the
 * deposit figures are CHF because that is the currency deposits are held in.
 * They are still separate facts and each field says which it is — a deposit
 * held is not a receivable, and adding the two would be a category error even
 * in one currency.
 *
 * `computedAt` travels with them because these are an aggregate over a moving
 * window: without it a cached summary cannot be told from a fresh one, and
 * `overdueChf` in particular is only true as of the instant it was computed.
 */
export const AdminFinanceSummarySchema = z.object({
    /** Everything issued and not yet received. */
    receivableChf: z.number().finite(),
    /** The part of `receivableChf` past its due date. */
    overdueChf: z.number().finite(),
    /** Client deposits currently held, CHF — see {@link FinanceClientSchema}. */
    depositsHeldChf: z.number().finite(),
    /**
     * Of those, the part owed back rather than committed against an invoice.
     * Nullable for the same reason as `FinanceClient.depositRefundableChf`:
     * nothing tracks refundability yet, and `null` beats a fabricated `0`.
     */
    depositsRefundableChf: z.number().finite().nullish(),
    /** Recognized revenue over `period`. */
    revenueChf: z.number().finite(),
    period: FinancePeriodSchema,
    computedAt: z.coerce.date(),
});
/* ───────────────────────── Invoice drawer (billing) ──────────────────────── */
/**
 * A won lot the invoice drawer can bill for.
 *
 * `bidChf` is the hammer price and stays CHF — the drawer's whole calculation
 * starts from it, and a CHF invoice carries it with `fx: null`.
 *
 * The frontend's single `meta` caption ("auction house and outcome line") is
 * split back into the two facts it was rendered from.
 */
export const InvoiceLotSchema = z.object({
    /** Short lot id, e.g. `3E19`. */
    lotId: z.string(),
    name: z.string(),
    auctionHouse: z.string(),
    wonAt: z.coerce.date(),
    /** Winning bid, CHF. */
    bidChf: z.number().finite(),
});
/**
 * What the invoice drawer needs about the client it is billing.
 *
 * `tier` replaces a boolean "is a dealer": it is the only thing that decides
 * the commission and subscription price, and a boolean cannot tell `dealer`
 * from `dealer_pro`.
 *
 * Every money field on this shape is CHF: the deposit is `users.biddingPower`
 * and each lot's `bidChf` is a hammer price. The draft is CHF too, so neither
 * is converted; the EUR-priced service fee is, once, with the conversion
 * recorded on its {@link InvoiceLineSchema} as an {@link FxConversionSchema}.
 */
export const InvoiceDraftClientSchema = z.object({
    userId: z.string(),
    name: z.string(),
    tier: PricingPlanTierSchema,
    /** Deposit available as prepayment for the draft, CHF. */
    depositChf: z.number().finite(),
    lots: z.array(InvoiceLotSchema),
});
//# sourceMappingURL=admin-finance.js.map